import React, { useState } from 'react';
import {
  Bot,
  ExternalLink,
  Play,
  RefreshCw,
  AlertCircle,
  Briefcase,
  Megaphone,
  Search,
  LayoutDashboard,
  Palette,
  Zap
} from 'lucide-react';

interface AIStudioTabProps {
  adminEmail: string;
}

// Agent info matching dashboard.py personalities
// Note: the per-agent color string is preserved as a brand identity marker for each persona.
const AGENTS = [
  {
    id: 'accountant',
    name: 'Al the Accountant',
    title: 'Chief Financial Analyst',
    icon: Briefcase,
    color: '#556B2F', // olive
    description: 'Profit margins, budgets, financial analysis'
  },
  {
    id: 'marketing',
    name: 'Manny the Marketer',
    title: 'B2B Marketing Strategist',
    icon: Megaphone,
    color: '#718096', // steel
    description: 'Campaigns, competitor intel, B2B strategy'
  },
  {
    id: 'seo',
    name: 'Sara the SEO Specialist',
    title: 'B2B SEO Expert',
    icon: Search,
    color: '#A88752', // brass
    description: 'Keywords, rankings, content optimization'
  },
  {
    id: 'strategist',
    name: 'Steph the Strategist',
    title: 'Social Media Strategist',
    icon: LayoutDashboard,
    color: '#B8451F', // ember
    description: 'Content calendar, briefs, audience analysis'
  },
  {
    id: 'creative',
    name: 'Chris the Creative',
    title: 'Creative Content Specialist',
    icon: Palette,
    color: '#8A3318', // ember-deep
    description: 'Visual concepts, ad designs, video ideas'
  },
];

// Default Streamlit URL (local development)
const STREAMLIT_URL = 'http://localhost:8501';

const AIStudioTab: React.FC<AIStudioTabProps> = ({ adminEmail }) => {
  const [showIframe, setShowIframe] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);

  const handleOpenStudio = () => {
    window.open(STREAMLIT_URL, '_blank', 'noopener,noreferrer');
  };

  const handleEmbedStudio = () => {
    setIframeLoading(true);
    setIframeError(false);
    setShowIframe(true);
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
  };

  const handleIframeError = () => {
    setIframeLoading(false);
    setIframeError(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-forge text-paper p-7">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 bg-ember flex items-center justify-center flex-shrink-0">
            <Bot className="w-7 h-7 text-paper-warm" strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-2">— AI department</p>
            <h2 className="font-editorial italic font-light text-3xl text-paper-warm tracking-tight">
              Machete Man AI Studio.
            </h2>
            <p className="text-paper/70 text-sm mt-1">5-agent department powered by CrewAI + Claude</p>
          </div>
        </div>
      </div>

      {/* Agent Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {AGENTS.map((agent) => {
          const Icon = agent.icon;
          return (
            <div
              key={agent.id}
              className="bg-paper-bright border p-5 transition-colors duration-150 hover:border-ink-300"
              style={{ borderColor: agent.color + '40' }}
            >
              <div
                className="w-10 h-10 flex items-center justify-center mb-3"
                style={{ backgroundColor: agent.color + '20' }}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: agent.color }} />
              </div>
              <h3 className="font-editorial italic text-base text-ink-950 tracking-tight">{agent.name}</h3>
              <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mt-1 mb-2">{agent.title}</p>
              <p className="text-xs text-ink-700">{agent.description}</p>
            </div>
          );
        })}
      </div>

      {/* Launch Options */}
      <div className="bg-paper-bright border border-ink-200 overflow-hidden">
        <div className="bg-paper px-6 py-4 border-b border-ink-200">
          <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
            <Zap className="w-4 h-4 text-ember" strokeWidth={1.5} />
            Launch AI Studio
          </h3>
        </div>

        <div className="p-6">
          {!showIframe ? (
            <div className="space-y-5">
              <p className="text-ink-700 text-sm">
                The AI Studio dashboard lets you interact with your AI agents directly.
                Assign tasks, view results, and manage your virtual marketing department.
              </p>

              <div className="bg-brass/10 border-l-2 border-brass p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-brass flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="text-sm text-ink-900">
                    <strong className="text-ink-950">Before launching:</strong> Make sure the Streamlit server is running:
                    <pre className="mt-2 bg-paper-bright border border-ink-200 p-3 text-xs font-mono overflow-x-auto text-ink-900">
                      cd macheteman{'\n'}
                      .\venv\Scripts\activate{'\n'}
                      streamlit run dashboard.py
                    </pre>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleOpenStudio}
                  className="flex-1 flex items-center justify-center gap-2 bg-ember hover:bg-ember-deep text-paper-warm px-5 py-3 text-sm font-medium transition-colors duration-150 ease-editorial"
                >
                  <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  Open in new window
                </button>
                <button
                  onClick={handleEmbedStudio}
                  className="flex-1 flex items-center justify-center gap-2 bg-forge hover:bg-forge-deep text-paper px-5 py-3 text-sm font-medium transition-colors duration-150 ease-editorial"
                >
                  <Play className="w-4 h-4" strokeWidth={1.5} />
                  Embed here
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Iframe Controls */}
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500">
                  {iframeLoading ? 'Loading AI Studio...' : 'AI Studio (embedded)'}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIframeLoading(true);
                      const iframe = document.getElementById('studio-iframe') as HTMLIFrameElement;
                      if (iframe) {
                        const currentSrc = iframe.src;
                        iframe.src = '';
                        iframe.src = currentSrc;
                      }
                    }}
                    className="p-2 hover:bg-paper border border-ink-200 hover:border-ink-300 transition-colors duration-150"
                    title="Refresh"
                  >
                    <RefreshCw className="w-4 h-4 text-ink-700" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={handleOpenStudio}
                    className="p-2 hover:bg-paper border border-ink-200 hover:border-ink-300 transition-colors duration-150"
                    title="Open in new window"
                  >
                    <ExternalLink className="w-4 h-4 text-ink-700" strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => setShowIframe(false)}
                    className="px-3 py-1.5 text-sm text-ink-700 hover:bg-paper border border-ink-200 hover:border-ink-300 transition-colors duration-150"
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Error State */}
              {iframeError && (
                <div className="bg-ember-soft/25 border-l-2 border-ember p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div className="text-sm text-ember-deep">
                      <strong>Connection failed.</strong> The Streamlit server may not be running.
                      <pre className="mt-2 bg-paper-bright border border-ink-200 p-3 text-xs font-mono text-ink-900">
                        cd macheteman && streamlit run dashboard.py
                      </pre>
                      <button
                        onClick={handleEmbedStudio}
                        className="mt-2 text-ember hover:text-ember-deep underline transition-colors duration-150"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Iframe */}
              <div
                className="relative overflow-hidden border border-ink-200"
                style={{ height: '700px' }}
              >
                {iframeLoading && (
                  <div className="absolute inset-0 bg-paper flex items-center justify-center z-10">
                    <div className="text-center">
                      <RefreshCw className="w-8 h-8 text-ember animate-spin mx-auto mb-2" strokeWidth={1.5} />
                      <p className="text-ink-700 text-sm">Connecting to AI Studio...</p>
                    </div>
                  </div>
                )}
                <iframe
                  id="studio-iframe"
                  src={STREAMLIT_URL}
                  className="w-full h-full"
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                  title="Machete Man AI Studio"
                  allow="clipboard-write"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Reference */}
      <div className="bg-paper-bright border border-ink-200 overflow-hidden">
        <div className="bg-paper px-6 py-4 border-b border-ink-200">
          <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight">Quick reference</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">CLI commands</p>
              <div className="bg-paper border border-ink-200 p-4 font-mono text-xs space-y-1.5 text-ink-900">
                <p><span className="text-ember">python main.py --sequential</span> # Full weekly cycle</p>
                <p><span className="text-ember">python main.py --financial</span> # Accountant only</p>
                <p><span className="text-ember">python main.py --creative "topic"</span> # Creative campaign</p>
                <p><span className="text-ember">python main.py --ask-marketing "..."</span> # Ask specific agent</p>
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Output locations</p>
              <div className="bg-paper border border-ink-200 p-4 text-sm space-y-2 text-ink-900">
                <p><strong className="text-ink-950">Generated images:</strong> macheteman/media/generated/</p>
                <p><strong className="text-ink-950">Video briefs:</strong> macheteman/media/generated/</p>
                <p><strong className="text-ink-950">Data files:</strong> macheteman/data/</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIStudioTab;
